/**
 * File Upload Constants
 * Centralized configuration for file upload limits and validations
 * Ensures consistency across the entire application
 */

// Maximum file size: 10MB (consistent across all file uploads)
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

// Maximum file size in MB (for display purposes)
export const MAX_FILE_SIZE_MB = 10;

// Allowed file types for document uploads
export const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'text/plain'
];

// Allowed file extensions for document uploads
export const ALLOWED_DOCUMENT_EXTENSIONS = [
  '.pdf', '.xls', '.xlsx', '.ppt', '.pptx', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.txt'
];

// Allowed file types for action point documents (without text files)
export const ALLOWED_ACTION_POINT_TYPES = [
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/jpg',
  'image/png'
];

// Allowed file extensions for action point documents
export const ALLOWED_ACTION_POINT_EXTENSIONS = [
  '.pdf', '.xls', '.xlsx', '.ppt', '.pptx', '.doc', '.docx', '.jpg', '.jpeg', '.png'
];

/**
 * Validate file size
 * @param {File} file - File to validate
 * @returns {Object} - { isValid: boolean, message: string }
 */
export const validateFileSize = (file) => {
  if (!file) {
    return { isValid: false, message: 'Please select a file' };
  }
  
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      message: `File size too large. Maximum size: ${MAX_FILE_SIZE_MB}MB`
    };
  }
  
  return { isValid: true, message: '' };
};

/**
 * Validate file type
 * @param {File} file - File to validate
 * @param {Array} allowedTypes - Array of allowed MIME types
 * @returns {Object} - { isValid: boolean, message: string }
 */
export const validateFileType = (file, allowedTypes = ALLOWED_DOCUMENT_TYPES) => {
  if (!file) {
    return { isValid: false, message: 'Please select a file' };
  }
  
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      message: 'File type not supported. Please upload a valid document, spreadsheet, presentation, image, or text file.'
    };
  }
  
  return { isValid: true, message: '' };
};

/**
 * Complete file validation (size + type)
 * @param {File} file - File to validate
 * @param {Array} allowedTypes - Array of allowed MIME types (optional)
 * @returns {Object} - { isValid: boolean, message: string }
 */
export const validateFile = (file, allowedTypes = ALLOWED_DOCUMENT_TYPES) => {
  const sizeValidation = validateFileSize(file);
  if (!sizeValidation.isValid) {
    return sizeValidation;
  }
  
  const typeValidation = validateFileType(file, allowedTypes);
  if (!typeValidation.isValid) {
    return typeValidation;
  }
  
  return { isValid: true, message: '' };
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size (e.g., "5.23 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

